import { ReactPlugin } from '@microsoft/applicationinsights-react-js'
import { ApplicationInsights, DistributedTracingModes } from '@microsoft/applicationinsights-web'

export const reactPlugin = new ReactPlugin()

let appInsights: ApplicationInsights | null = null

interface IAppInsightsInitParams {
  connectionString: string
  ambiente: string
  region: string
  /** BASE_URL del runtime config: solo a ese host se le mandan las cabeceras de correlación. */
  backendUrl?: string
  /**
   * URL absoluta del relevo del mismo origen (`/api/metricas`). Con ella el SDK no habla con Azure sino con
   * la propia app, que reenvía; así la telemetría no se pierde en los navegadores con bloqueadores de
   * privacidad. Sin ella, el SDK manda directo al IngestionEndpoint de la cadena.
   */
  endpointRelevo?: string
}

/**
 * Host al que el SDK puede mandar `traceparent` / `Request-Id`. Se limita al backend a propósito:
 * si las cabeceras fueran a Entra o a Courier, sus preflight CORS las rechazarían.
 */
export const hostsDeCorrelacion = (backendUrl?: string): string[] => {
  if (!backendUrl) {
    return []
  }
  try {
    return [new URL(backendUrl).hostname]
  } catch {
    return []
  }
}

export const initializeAppInsights = ({
  connectionString,
  ambiente,
  region,
  backendUrl,
  endpointRelevo,
}: IAppInsightsInitParams): void => {
  // Solo en navegador y una sola vez por sesión
  if (appInsights || typeof window === 'undefined') {
    return
  }

  if (!connectionString) {
    // Sin cadena no se inicializa. Antes caía a un recurso legado y la telemetría del navegador de todos
    // los ambientes, producción incluida, terminaba en el componente de QA.
    console.warn('APP_APPINSIGHTS_CONNECTION_STRING no está configurada: la telemetría del navegador queda apagada.')

    return
  }

  const hosts = hostsDeCorrelacion(backendUrl)

  appInsights = new ApplicationInsights({
    config: {
      connectionString,
      // Prioridad máxima sobre el endpoint de la cadena: es la opción prevista por el SDK para relevos propios.
      ...(endpointRelevo && { userOverrideEndpointUrl: endpointRelevo }),
      enableAutoRouteTracking: true,
      enableUnhandledPromiseRejectionTracking: true,
      // Correlación navegador → backend: con W3C el backend adopta el trace id del navegador y la petición
      // del servidor comparte operation_Id con la llamada que la originó, así la vista de transacción de
      // Application Insights no se corta en el frontend.
      distributedTracingMode: DistributedTracingModes.AI_AND_W3C,
      enableCorsCorrelation: hosts.length > 0,
      correlationHeaderDomains: hosts,
      extensions: [reactPlugin],
    },
  })

  appInsights.loadAppInsights()

  // Etiquetar toda la telemetría con el ambiente y la región para poder filtrar en el portal
  appInsights.addTelemetryInitializer((item) => {
    item.tags = item.tags || []
    item.tags['ai.cloud.role'] = `xts-frontend-${region.toLowerCase()}-${ambiente}`
    item.data = item.data || {}
    item.data.ambiente = ambiente
    item.data.region = region
  })
}

// El username no admite caracteres reservados (,;=| ) en el contexto autenticado
const sanitizarUsuario = (username: string): string => username.replace(/[,;=| ]+/g, '_')

export const setAppInsightsUser = (username: string): void => {
  if (appInsights && username) {
    appInsights.setAuthenticatedUserContext(sanitizarUsuario(username), undefined, true)
  }
}

export const clearAppInsightsUser = (): void => {
  appInsights?.clearAuthenticatedUserContext()
}

export const getAppInsights = (): ApplicationInsights | null => appInsights
